import { useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import axiosInstance from "../config/axios";
import { AuthContext } from "../Contexts/AuthContext";

export const getTransformedProducts = (products) => {
  return products.map((product) => {
    const fallbackImages =
      product.variants.find((v) => v.images !== null)?.images ?? null;
    return {
      ...product,
      variants: product.variants.map((variant) => ({
        ...variant,
        images: variant.images === null ? fallbackImages : variant.images,
      })).sort((a, b) => Number(a.price) - Number(b.price)),
    };
  });
};

// Anything but a list (an HTML error page from a proxy, say) is thrown, so the
// query fails and is retried instead of being cached and persisted as data.
export const asList = (data) => {
  if (!Array.isArray(data)) throw new Error("Unexpected catalog response");
  return data;
};

// the endpoints answer either a bare list or a paginated { results } page
export const toProductList = (data) => getTransformedProducts(asList(data?.results ?? data));

// Cached GET against a public catalog endpoint. The axios interceptor appends the
// delivery zip to every request and stock/prices differ per zip, so the zip is
// part of the cache key - changing location never shows another area's products.
export const useCatalogQuery = (path, { params, transform = (data) => data, enabled = true } = {}) => {
  const { location } = useContext(AuthContext);

  const query = useQuery({
    queryKey: ["catalog", path, params ?? null, location?.zip ?? null],
    queryFn: async ({ signal }) => {
      const response = await axiosInstance.get(path, { params, signal });
      return transform(response.data);
    },
    enabled,
  });

  return { data: query.data, loading: query.isPending, error: query.error };
};

export const useSubCategories = () => {
  const { data, loading } = useCatalogQuery("/public/subcategories", { transform: asList });
  return { categories: data ?? [], loading };
};

export const useProductsBySubCategory = (subCategoryId) => {
  const { data, loading } = useCatalogQuery("/public/products", {
    params: { sub_category: subCategoryId },
    transform: toProductList,
    enabled: !!subCategoryId,
  });
  return { products: data ?? [], loading };
};

export const useDiscountedProducts = () => {
  const { data, loading } = useCatalogQuery("/public/products/filter/discounted", { transform: toProductList });
  return { products: data ?? [], loading };
};

export const useNewProducts = () => {
  const { data, loading } = useCatalogQuery("/public/products/filter/new", { transform: toProductList });
  return { products: data ?? [], loading };
};

export const useRecommendedProducts = () => {
  const { data, loading } = useCatalogQuery("/public/products", {
    params: { search_attr: "recommended" },
    transform: toProductList,
  });
  return { products: data ?? [], loading };
};

// products listed by a comma separated id string, as a product's fbt/cab/rp
// fields store them
export const useProductsById = (ids) => {
  const { data } = useCatalogQuery("/public/products/list/id", {
    params: { ids },
    transform: asList,
    enabled: !!ids,
  });
  return data ?? [];
};

export const useBrands = () => {
  const { data, loading } = useCatalogQuery("/public/brands", { transform: asList });
  return { brands: data ?? [], loading };
};

export const useCategories = () => {
  const { data, loading } = useCatalogQuery("/public/categories", { transform: asList });
  return { categories: data ?? [], loading };
};
